import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { createPortal } from "react-dom";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
} from "recharts";
import { Users, MapPin, CheckCircle2, AlertTriangle, TrendingUp, ScanFace, BellRing, Loader2, X, Send } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const COLORS = { present: "#10B981", review: "#F59E0B", absent: "#EF4444" };
const SITE_COLORS = ["#6366F1", "#3B82F6", "#06B6D4", "#8B5CF6", "#F59E0B", "#EC4899"];

function KpiCard({ icon: Icon, label, value, tint, trend, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 hover:border-slate-300 hover:shadow-md transition-all" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className={`grid place-items-center h-11 w-11 rounded-xl ${tint}`}>
          <Icon className="h-5 w-5" />
        </div>
        {trend != null && (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600">
            <TrendingUp className="h-3.5 w-3.5" /> {trend}
          </span>
        )}
      </div>
      <p className="mt-4 text-3xl font-head font-black text-slate-900 tracking-tight">{value}</p>
      <p className="text-sm text-slate-500 mt-0.5">{label}</p>
    </div>
  );
}

function ChartCard({ title, subtitle, children, className = "", testid }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-xl shadow-sm p-5 ${className}`} data-testid={testid}>
      <div className="mb-4">
        <h3 className="font-head text-lg font-semibold text-slate-900 tracking-tight">{title}</h3>
        {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-lg px-3 py-2 text-xs">
      {label && <p className="font-semibold text-slate-700 mb-1">{label}</p>}
      {payload.map((p) => (
        <p key={p.name} className="text-slate-600 capitalize flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color || p.fill }} />
          {p.name}: <span className="font-semibold text-slate-800">{p.value}</span>
        </p>
      ))}
    </div>
  );
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function Dashboard() {
  const { data: employees = [] } = useQuery({ queryKey: ["employees"], queryFn: () => api.get("/admin/employees") });
  const { data: quota } = useQuery({ queryKey: ["quota"], queryFn: () => api.get("/admin/quota") });
  const { data: sites = [] } = useQuery({ queryKey: ["sites"], queryFn: () => api.get("/admin/sites") });
  const { data: attendance = [] } = useQuery({ queryKey: ["attendance"], queryFn: () => api.get("/admin/attendance?limit=1000") });
  const { data: reviews = [] } = useQuery({ queryKey: ["reviews"], queryFn: () => api.get("/admin/reviews") });
  const [showTestPush, setShowTestPush] = useState(false);

  const clockIns = attendance.filter((a) => a.type === "in");
  const presentToday = clockIns.filter((a) => a.day === todayISO() && a.status === "present").length;

  // Trend: last 7 days present / review
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const iso = d.toISOString().slice(0, 10);
    const dayEvents = clockIns.filter((a) => a.day === iso);
    days.push({
      day: d.toLocaleDateString([], { weekday: "short" }),
      present: dayEvents.filter((e) => e.status === "present").length,
      review: dayEvents.filter((e) => e.status === "review").length,
    });
  }

  // Status breakdown
  const breakdown = [
    { name: "present", value: clockIns.filter((a) => a.status === "present").length },
    { name: "review", value: clockIns.filter((a) => a.status === "review").length },
    { name: "absent", value: clockIns.filter((a) => a.status === "absent").length },
  ].filter((x) => x.value > 0);

  // Employees per site
  const siteCounts = sites.map((s) => ({
    name: s.name,
    employees: employees.filter((e) => e.site_id === s.id).length,
  }));
  const unassigned = employees.filter((e) => !e.site_id).length;
  if (unassigned > 0) siteCounts.push({ name: "Unassigned", employees: unassigned });

  const audited = clockIns.filter((a) => a.audited).length;
  const matched = clockIns.filter((a) => a.audit_result?.match === true).length;

  const hasBreakdown = breakdown.length > 0;

  const quotaLabel = quota
    ? `${quota.used} / ${quota.quota == null ? "∞" : quota.quota}`
    : employees.length;
  const quotaSubLabel = quota?.master ? "Employees (all admins)" : "Employees Used / Quota";

  return (
    <div className="space-y-6 animate-fade-up" data-testid="dashboard-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-head text-xl font-bold text-slate-900 tracking-tight">Overview</h2>
          <p className="text-sm text-slate-500">Live workforce snapshot · Asia / Muscat</p>
        </div>
        <button
          onClick={() => setShowTestPush(true)}
          data-testid="open-test-push-btn"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 active:scale-[0.99] transition shadow-sm"
        >
          <BellRing className="h-4 w-4 text-indigo-300" /> Send Test Push
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testid="kpi-total-employees" icon={Users} label={quotaSubLabel} value={quotaLabel} tint="bg-indigo-50 text-indigo-600" />
        <KpiCard testid="kpi-total-sites" icon={MapPin} label="Active Sites" value={sites.length} tint="bg-sky-50 text-sky-600" />
        <KpiCard testid="kpi-present-today" icon={CheckCircle2} label="Present Today" value={presentToday} tint="bg-emerald-50 text-emerald-600" trend="+3%" />
        <KpiCard testid="kpi-pending-reviews" icon={AlertTriangle} label="Pending Reviews" value={reviews.length} tint="bg-amber-50 text-amber-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ChartCard testid="chart-trend" title="Attendance Trend" subtitle="Clock-ins over the last 7 days" className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={days} margin={{ left: -20, right: 10, top: 10 }}>
              <defs>
                <linearGradient id="gPresent" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.present} stopOpacity={0.35} />
                  <stop offset="95%" stopColor={COLORS.present} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gReview" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.review} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={COLORS.review} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="present" stroke={COLORS.present} strokeWidth={2.5} fill="url(#gPresent)" />
              <Area type="monotone" dataKey="review" stroke={COLORS.review} strokeWidth={2.5} fill="url(#gReview)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard testid="chart-breakdown" title="Status Breakdown" subtitle="All-time clock-in verification">
          {hasBreakdown ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={breakdown} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={62} outerRadius={95} paddingAngle={3}>
                  {breakdown.map((e) => (
                    <Cell key={e.name} fill={COLORS[e.name]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend verticalAlign="bottom" iconType="circle" formatter={(v) => <span className="text-xs text-slate-500 capitalize">{v}</span>} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] grid place-items-center text-sm text-slate-400">No clock-in data yet</div>
          )}
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ChartCard testid="chart-sites" title="Employees per Site" subtitle="Workforce distribution" className="lg:col-span-2">
          {siteCounts.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={siteCounts} margin={{ left: -20, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f8fafc" }} />
                <Bar dataKey="employees" radius={[6, 6, 0, 0]} maxBarSize={56}>
                  {siteCounts.map((_, i) => (
                    <Cell key={i} fill={SITE_COLORS[i % SITE_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[260px] grid place-items-center text-sm text-slate-400">No sites yet</div>
          )}
        </ChartCard>

        <ChartCard testid="chart-ai" title="AI Face-Match Audits" subtitle="Claude verification results">
          <div className="flex flex-col items-center justify-center h-[260px] gap-6">
            <div className="grid place-items-center h-24 w-24 rounded-full bg-sky-50 border-4 border-sky-100">
              <ScanFace className="h-10 w-10 text-sky-600" />
            </div>
            <div className="grid grid-cols-2 gap-4 w-full">
              <div className="text-center rounded-xl bg-slate-50 border border-slate-200 py-3">
                <p className="text-2xl font-head font-black text-slate-900">{audited}</p>
                <p className="text-xs text-slate-500">Audited</p>
              </div>
              <div className="text-center rounded-xl bg-emerald-50 border border-emerald-200 py-3">
                <p className="text-2xl font-head font-black text-emerald-700">{matched}</p>
                <p className="text-xs text-emerald-600">Matched</p>
              </div>
            </div>
          </div>
        </ChartCard>
      </div>

      <TestPushModal open={showTestPush} onClose={() => setShowTestPush(false)} employees={employees} />
    </div>
  );
}

function TestPushModal({ open, onClose, employees }) {
  const [target, setTarget] = useState("me");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const send = async () => {
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post("/admin/push/test", target === "me" ? {} : { user_id: target });
      setResult(r);
      if (!r.push_ready) {
        toast.info("Push isn't enabled yet — the key is still pending.");
      } else if (r.devices === 0) {
        toast.warning(`No registered devices for ${r.target_name}.`);
      } else {
        toast.success(`Test push sent to ${r.sent}/${r.devices} device(s).`);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100]">
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden" data-testid="test-push-modal">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
            <h3 className="font-head text-lg font-bold text-slate-900 flex items-center gap-2">
              <BellRing className="h-5 w-5 text-indigo-600" /> Send Test Push
            </h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
          </div>
          <div className="p-6 space-y-4">
            <p className="text-sm text-slate-500">Send a test notification to verify push delivery to a mobile device.</p>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Recipient</label>
              <select
                data-testid="test-push-target"
                value={target}
                onChange={(e) => { setTarget(e.target.value); setResult(null); }}
                className="w-full h-11 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
              >
                <option value="me">My own devices</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>{e.name} ({e.staff_id})</option>
                ))}
              </select>
            </div>

            {result && (
              <div
                className={`rounded-lg px-4 py-3 text-sm border ${
                  !result.push_ready
                    ? "bg-amber-50 border-amber-200 text-amber-800"
                    : result.devices === 0
                    ? "bg-slate-50 border-slate-200 text-slate-600"
                    : "bg-emerald-50 border-emerald-200 text-emerald-800"
                }`}
                data-testid="test-push-result"
              >
                {!result.push_ready ? (
                  <p><b>Push not enabled yet.</b> The EMERGENT_PUSH_KEY is still a placeholder — real delivery starts once it's set (after deployment). Registered devices for {result.target_name}: <b>{result.devices}</b>.</p>
                ) : result.devices === 0 ? (
                  <p>No registered devices found for <b>{result.target_name}</b>. The mobile app must register a push token first.</p>
                ) : (
                  <p>Sent to <b>{result.sent}</b> of <b>{result.devices}</b> device(s) for <b>{result.target_name}</b>.</p>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Close</button>
              <button
                onClick={send}
                disabled={loading}
                data-testid="test-push-send-btn"
                className="h-10 px-4 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 inline-flex items-center gap-2"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send Test
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
